/**
 * Staffology Design System — Base Components
 * Version: 2.0.0
 *
 * 13 base components + form controls (Checkbox/Toggle/Radio).
 * All in styled-components + TypeScript, all states implemented,
 * all tokens via theme.
 *
 * Components in this file:
 *   - Table (with density prop, sortable headers, selection)
 *   - Modal (with focus trap, ESC close, backdrop)
 *   - Tabs (pill + underline + segmented variants)
 *   - Select (single value from short list)
 *   - Combobox (searchable, async-friendly)
 *   - DatePicker (single date)
 *   - DateRangePicker (start + end)
 *   - Toast (with ToastProvider + useToast hook)
 *   - Tooltip
 *   - Dropdown (action menu)
 *   - Pagination
 *   - Skeleton
 *   - Breadcrumb
 *   - Checkbox / Radio / Toggle (mass-instance → navy)
 *
 * Each component documents its dependencies. lucide-react is
 * assumed available (already in v1.1 contract).
 */

import React, {
  forwardRef,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
  useContext,
  useId,
  createContext,
  type HTMLAttributes,
  type ReactNode,
  type InputHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
} from 'react'
import {createPortal} from 'react-dom'
import styled, {css, keyframes} from 'styled-components'
import {X} from 'lucide-react'
import {VisuallyHidden, Spinner, Stack} from './Primitives'
import {useAdaptiveDropdownPosition} from './dropdownPositioning'
import {isTextTruncated, renderedTextOf, repeatsChildText} from './textTruncation'
import {sideWithRoom, type TooltipSide} from './tooltipPlacement'
import {useOutsidePress} from './outsidePress'

/* ============================================================
   TABLE
   Density-aware. Header is divider-only (no bg). Row selection,
   sort indicators, and sticky header supported.
   ============================================================ */

type TableDensity = 'comfortable' | 'data'

export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  density?: TableDensity
  stickyHeader?: boolean
}

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  border: 1px solid var(--border-subtle);
`

const StyledTable = styled.table<{$density: TableDensity}>`
  width: 100%;
  border-collapse: collapse;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--text-primary);

  th,
  td {
    text-align: left;
    padding: ${({$density}) =>
      $density === 'data' ? 'var(--row-data-padding)' : 'var(--row-comfortable-padding)'};
    vertical-align: middle;
  }

  tbody tr {
    border-bottom: 1px solid var(--divider);
    transition: background var(--motion-fast) var(--motion-ease);
  }

  tbody tr:last-child {
    border-bottom: none;
  }

  tbody tr:hover {
    background: var(--bg-subtle);
  }

  tbody tr[aria-selected='true'] {
    background: var(--brand-50);
    box-shadow: inset 3px 0 0 var(--brand-cta);
  }

  tbody tr[data-readonly='true'] {
    background: var(--bg-subtle);
    color: var(--text-secondary);
  }
`

const StyledThead = styled.thead<{$sticky: boolean}>`
  border-bottom: 1px solid var(--divider);
  background: var(--bg-card);

  ${({$sticky}) =>
    $sticky &&
    css`
      position: sticky;
      top: 0;
      z-index: var(--z-sticky);
      box-shadow: var(--shadow-strong);
    `}

  th {
    font-size: var(--fs-micro);
    font-weight: var(--fw-semibold);
    color: var(--text-secondary);
    text-transform: uppercase;
    letter-spacing: var(--ls-wide);
    white-space: nowrap;
  }
`

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({density = 'comfortable', stickyHeader = false, children, ...rest}, ref) => (
    <TableWrapper>
      <StyledTable ref={ref} $density={density} {...rest}>
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement(child) &&
            (child.type as React.ComponentType)?.displayName === 'TableHead'
          ) {
            return React.cloneElement(child as React.ReactElement, {$sticky: stickyHeader})
          }
          return child
        })}
      </StyledTable>
    </TableWrapper>
  ),
)
Table.displayName = 'Table'

export const TableHead = ({
  $sticky,
  ...rest
}: {$sticky?: boolean} & React.HTMLAttributes<HTMLTableSectionElement>) => (
  <StyledThead $sticky={$sticky ?? false} {...rest} />
)
TableHead.displayName = 'TableHead'

export const TableBody = styled.tbody``
export const TableRow = styled.tr``

export interface TableHeaderCellProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean
  sortDirection?: 'asc' | 'desc' | null
  onSort?: () => void
}

const SortButton = styled.button<{$active: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  cursor: pointer;
  padding: 0;

  svg {
    width: 12px;
    height: 12px;
    color: ${({$active}) => ($active ? 'var(--brand-cta)' : 'var(--text-disabled)')};
  }

  &:focus-visible {
    outline: 2px solid var(--brand-cta);
    outline-offset: 2px;
    border-radius: var(--radius-sm);
  }
`

export const TableHeaderCell = ({
  sortable,
  sortDirection,
  onSort,
  children,
  ...rest
}: TableHeaderCellProps) => (
  <th
    {...rest}
    aria-sort={
      sortDirection === 'asc'
        ? 'ascending'
        : sortDirection === 'desc'
        ? 'descending'
        : sortable
        ? 'none'
        : undefined
    }>
    {sortable ? (
      <SortButton type="button" onClick={onSort} $active={sortDirection !== null}>
        {children}
        <SortChevron direction={sortDirection} />
      </SortButton>
    ) : (
      children
    )}
  </th>
)

const SortChevron = ({direction}: {direction: 'asc' | 'desc' | null | undefined}) => (
  <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
    {direction === 'asc' ? (
      <path
        d="M3 7.5L6 4.5L9 7.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : direction === 'desc' ? (
      <path
        d="M3 4.5L6 7.5L9 4.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ) : (
      <>
        <path
          d="M3 5L6 2L9 5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M3 7L6 10L9 7"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </>
    )}
  </svg>
)

export const TableCell = styled.td<TdHTMLAttributes<HTMLTableCellElement>>``

/* Empty cell helper. Use this instead of leaving cells blank. */
export const EmptyCell = styled.span`
  color: var(--text-disabled);
  &::before {
    content: '—';
  }
`

/* ============================================================
   MODAL
   With focus trap, ESC close, backdrop click close (optional),
   and proper aria attributes.
   ============================================================ */

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl'

export interface ModalProps {
  open: boolean
  onClose: () => void
  /** Plain text in most cases; a node when the header needs an eyebrow above the title. */
  title: ReactNode
  size?: ModalSize
  /** Whether clicking the backdrop closes the modal. Default true. */
  closeOnBackdropClick?: boolean
  /** Whether ESC closes the modal. Default true. */
  closeOnEsc?: boolean
  children: ReactNode
  footer?: ReactNode
  /** When true, the modal can't be dismissed except via explicit action.
   *  Use for destructive confirmations where accidental dismiss is bad. */
  blocking?: boolean
  /** Fixed tall shell (≤780px, never past the viewport) whose body scrolls inside. */
  fullHeight?: boolean
  /** Close button: `sm` (default) 32px, `lg` the 44px touch target. */
  closeSize?: 'sm' | 'lg'
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: var(--overlay-backdrop);
  z-index: var(--z-modal);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  animation: ${fadeIn} var(--motion-base) var(--motion-ease);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const modalSizes = {
  sm: '420px',
  md: '560px',
  lg: '720px',
  xl: '960px',
  '2xl': '1040px',
}

const fullHeightShell = `height: min(780px, calc(100vh - var(--space-16))); max-height: calc(100vh - var(--space-16)); overflow: hidden; border: var(--border-thin) solid var(--border-subtle);`

const ModalShell = styled.div<{$size: ModalSize; $fullHeight: boolean}>`
  background: var(--bg-elevated);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-modal);
  width: 100%;
  max-width: ${({$size}) => modalSizes[$size]};
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  ${({$fullHeight}) => $fullHeight && fullHeightShell}
  animation: ${slideUp} var(--motion-base) var(--motion-ease);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
  padding: var(--space-5) var(--space-6) var(--space-4);
  border-bottom: 1px solid var(--divider);
`

const ModalTitle = styled.h2`
  margin: 0;
  font-size: var(--fs-h3);
  font-weight: var(--fw-semibold);
  color: var(--primary-700);
  letter-spacing: var(--ls-tight);
`

const CloseButton = styled.button<{$size: 'sm' | 'lg'}>`
  background: none;
  border: none;
  width: ${({$size}) => ($size === 'lg' ? 'var(--input-height-md)' : '32px')};
  height: ${({$size}) => ($size === 'lg' ? 'var(--input-height-md)' : '32px')};
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-secondary);
  border-radius: var(--radius-sm);

  &:hover {
    background: var(--bg-hover);
    color: var(--text-primary);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

const ModalBody = styled.div`
  padding: var(--space-6);
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
  padding: var(--space-4) var(--space-6);
  border-top: 1px solid var(--divider);
`

let openModalScrollLocks = 0 /* open Modals holding the body scroll lock */

export const Modal = ({
  open,
  onClose,
  title,
  size = 'md',
  closeOnBackdropClick = true,
  closeOnEsc = true,
  blocking = false,
  children,
  footer,
  fullHeight = false,
  closeSize = 'sm',
}: ModalProps) => {
  const shellRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)
  const titleId = useId() /* per instance: two mounted modals never share a title id */

  /* Escape closes, unless something inside already used it (an open Select list closes alone). */
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented && closeOnEsc && !blocking) onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, closeOnEsc, blocking, onClose])

  /* Focus: the shell on open, back to the opener on close. The body scroll lock is counted, so only
     the last open modal to close releases it, and a closed mounted one never clears another's. */
  useEffect(() => {
    if (!open) {
      previousFocusRef.current?.focus()
      return
    }
    previousFocusRef.current = document.activeElement as HTMLElement
    /* A frame later, and only if focus has not already moved inside (a click on a control in that
       frame keeps its focus); cancelled if the modal closes first. */
    const focusFrame = requestAnimationFrame(() => {
      const shell = shellRef.current
      if (shell && !shell.contains(document.activeElement)) shell.focus()
    })
    openModalScrollLocks += 1
    document.body.style.overflow = 'hidden'
    return () => {
      cancelAnimationFrame(focusFrame)
      openModalScrollLocks -= 1
      if (openModalScrollLocks === 0) document.body.style.overflow = ''
    }
  }, [open])

  /* Focus trap over what Tab reaches (no disabled, hidden or tabindex -1 control, bar the focused
     one, which may just have been disabled): the ends wrap, and from the shell (focused on open)
     Tab goes to the first and Shift+Tab to the last. */
  useEffect(() => {
    if (!open) return
    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !shellRef.current) return
      const active = document.activeElement
      const focusables = Array.from(
        shellRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]',
        ),
      ).filter((el) => el === active || (el.tabIndex >= 0 && !el.matches(':disabled')))
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const onShell = active === shellRef.current
      if (!first || !last) {
        if (onShell) e.preventDefault() /* nothing to reach: Tab stays on the shell */
      } else if (e.shiftKey && (onShell || active === first)) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && (onShell || active === last)) {
        e.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [open])

  if (!open) return null

  /* Portaled to body: the fixed backdrop resolves against the viewport only while no ancestor has
     a transform, filter, perspective or `contain` (a hover-lifted card did: off-centre, clipped).
     NOTE: a portal moves the DOM node, not the React tree, so clicks inside still bubble to React
     ancestors; a modal declared in a clickable card must stop propagation. */
  return createPortal(
    <Backdrop
      onClick={(e) => {
        if (e.target === e.currentTarget && closeOnBackdropClick && !blocking) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}>
      <ModalShell ref={shellRef} $size={size} $fullHeight={fullHeight} tabIndex={-1}>
        <ModalHeader>
          <ModalTitle id={titleId}>{title}</ModalTitle>
          {!blocking && (
            <CloseButton onClick={onClose} aria-label="Close modal" $size={closeSize}>
              <X size={closeSize === 'lg' ? 20 : 16} strokeWidth={1.75} aria-hidden />
            </CloseButton>
          )}
        </ModalHeader>
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalShell>
    </Backdrop>,
    document.body,
  )
}

/* ============================================================
   TABS
   Variants: pill (page-level), underline (in-card), segmented (a
   compact bordered pill track). Keys: the arrows, Home and End.
   ============================================================ */

type TabsVariant = 'pill' | 'underline' | 'segmented'

export interface TabItem {
  id: string
  label: string
  disabled?: boolean
  badge?: ReactNode
  /**
   * `false`: the arrows, Home and End only focus this tab, and a click, Enter or Space selects it.
   * For a tab that opens a popup (the staffer page's Custom period). Default true.
   */
  selectOnFocus?: boolean
  /** Set to this tab's button, e.g. to anchor a popup under it and return focus to it. */
  buttonRef?: React.MutableRefObject<HTMLButtonElement | null>
  'aria-haspopup'?: 'dialog'
  'aria-expanded'?: boolean
  /** A name that says more than the label; it must contain the visible label (WCAG 2.5.3). */
  'aria-label'?: string
}

export type TabsOverflow = 'wrap' | 'scroll'

export interface TabsProps {
  items: TabItem[]
  activeId: string
  onChange: (id: string) => void
  variant?: TabsVariant
  /**
   * Overflow behavior when tabs exceed the container width.
   *  - `'wrap'` (default): tabs wrap to multiple rows. Preserves current behavior.
   *  - `'scroll'`: tabs stay on one row; the strip scrolls horizontally (native
   *    scroll, scrollbar hidden). Use this for surfaces with many tabs that
   *    must remain visually compact (e.g. CandidateInformation's 13 tabs).
   */
  overflow?: TabsOverflow
  'aria-label'?: string
  /** On the tab list; each tab gets `${data-testid}-tab-${id}`. */
  'data-testid'?: string
  /** Forwarded to the tab list — enables styled(Tabs) overrides. */
  className?: string
}

const TabsList = styled.div<{$variant: TabsVariant; $overflow: TabsOverflow}>`
  display: ${({$variant}) => ($variant === 'segmented' ? 'inline-flex' : 'flex')};
  /* Pill: 8px between pills. Underline: small but non-zero gap so tabs
     have breathing room instead of touching (modern tab-strip default).
     Segmented: the segments sit edge to edge inside the track. */
  gap: ${({$variant}) => ({pill: '8px', underline: 'var(--space-2)', segmented: '0'}[$variant])};
  ${({$variant}) =>
    $variant === 'underline' &&
    css`
      border-bottom: 1px solid var(--divider);
    `}
  ${({$variant}) =>
    $variant === 'segmented' &&
    css`
      border: var(--border-thin) solid var(--border-default);
      border-radius: var(--radius-pill);
      background: var(--bg-card);
      padding: calc(var(--space-1) / 2);
    `}
  ${({$overflow}) =>
    $overflow === 'scroll' &&
    css`
      flex-wrap: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      scroll-behavior: smooth;
      /* Hide the scrollbar chrome; native scroll still works (touchpad, drag, keyboard). */
      scrollbar-width: none;
      -ms-overflow-style: none;
      &::-webkit-scrollbar {
        display: none;
      }
      /* Prevent active-tab underline from being clipped by overflow. */
      & > button {
        flex-shrink: 0;
      }
    `}
`

const tabVariantStyles = (variant: TabsVariant, active: boolean) => {
  switch (variant) {
    case 'pill':
      return css`
        padding: var(--space-2) var(--space-4);
        border-radius: var(--radius-full);
        background: ${active ? 'var(--brand-cta)' : 'transparent'};
        color: ${active ? 'var(--text-on-brand)' : 'var(--neutral-700)'};

        &:hover:not(:disabled) {
          background: ${active ? 'var(--brand-hover)' : 'var(--bg-hover)'};
        }
      `
    case 'segmented':
      return css`
        min-height: var(--space-6);
        padding: 0 var(--space-3);
        justify-content: center;
        white-space: nowrap;
        border-radius: var(--radius-pill);
        background: ${active ? 'var(--brand-cta)' : 'transparent'};
        color: ${active ? 'var(--text-on-brand)' : 'var(--neutral-700)'};

        &:hover:not(:disabled) {
          background: ${active ? 'var(--brand-cta-hover)' : 'var(--bg-hover)'};
        }
      `
    default: // 'underline'
      return css`
        padding: var(--space-3) var(--space-1);
        background: transparent;
        color: ${active ? 'var(--brand-cta)' : 'var(--text-secondary)'};
        border-bottom: 2px solid ${active ? 'var(--brand-cta)' : 'transparent'};
        margin-bottom: -1px;

        &:hover:not(:disabled) {
          color: ${active ? 'var(--brand-cta)' : 'var(--text-primary)'};
        }
      `
  }
}

/* Segmented: caption size, semibold when active, the DS focus ring. */
const TabButton = styled.button<{$variant: TabsVariant; $active: boolean}>`
  font-family: var(--font-sans);
  font-size: ${({$variant}) => ($variant === 'segmented' ? 'var(--fs-caption)' : 'var(--fs-body)')};
  font-weight: ${({$variant, $active}) =>
    $variant !== 'segmented'
      ? 'var(--fw-medium)'
      : $active
      ? 'var(--fw-semibold)'
      : 'var(--fw-regular)'};
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all var(--motion-fast) var(--motion-ease);

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${({$variant}) =>
      $variant === 'segmented' ? 'var(--focus-ring)' : '0 0 0 3px var(--brand-100)'};
  }

  ${({$variant, $active}) => tabVariantStyles($variant, $active)}
`

export const Tabs = ({
  items,
  activeId,
  onChange,
  variant = 'pill',
  overflow = 'wrap',
  'data-testid': testId,
  ...rest
}: TabsProps) => {
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map())

  // Roving focus over the enabled tabs (moving also selects, except onto a tab whose
  // selectOnFocus is false): the arrows wrap, Home and End jump to the first and last.
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const enabled = items.filter((i) => !i.disabled)
    const at = enabled.findIndex((i) => i.id === items[index]?.id)
    const next = new Map<string, TabItem | undefined>([
      ['ArrowRight', enabled[(at + 1) % enabled.length]],
      ['ArrowLeft', enabled[(at - 1 + enabled.length) % enabled.length]],
      ['Home', enabled[0]],
      ['End', enabled[enabled.length - 1]],
    ]).get(e.key)
    if (!next) return
    e.preventDefault()
    if (next.id !== activeId && next.selectOnFocus !== false) onChange(next.id)
    tabRefs.current.get(next.id)?.focus()
  }

  return (
    <TabsList $variant={variant} $overflow={overflow} role="tablist" {...rest} data-testid={testId}>
      {items.map((item, idx) => (
        <TabButton
          key={item.id}
          ref={(el) => {
            if (el) tabRefs.current.set(item.id, el)
            if (item.buttonRef) item.buttonRef.current = el
          }}
          type="button"
          role="tab"
          aria-selected={item.id === activeId}
          aria-haspopup={item['aria-haspopup']}
          aria-expanded={item['aria-expanded']}
          aria-label={item['aria-label']}
          aria-disabled={item.disabled}
          disabled={item.disabled}
          tabIndex={item.id === activeId ? 0 : -1}
          data-testid={testId ? `${testId}-tab-${item.id}` : undefined}
          $variant={variant}
          $active={item.id === activeId}
          onClick={() => !item.disabled && onChange(item.id)}
          onKeyDown={(e) => handleKeyDown(e, idx)}>
          {item.label}
          {item.badge}
        </TabButton>
      ))}
    </TabsList>
  )
}

/* ============================================================
   SELECT (native-styled, single value, short list)
   For long or searchable lists, use Combobox.
   ============================================================ */

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
  /**
   * Optional rich display node rendered in place of the plain `label` text
   * in both the closed trigger and the open option row (e.g. a role name
   * with category/system badges). `label` is still used for the native
   * <option>, accessibility, and the truncation tooltip a cut-off row shows
   * (STFFLG-4236), so keep it a plain string.
   */
  labelContent?: React.ReactNode
}

export interface SelectProps extends Omit<InputHTMLAttributes<HTMLSelectElement>, 'size'> {
  label?: string
  helperText?: string
  errorText?: string
  /**
   * Error state without a built-in message, for callers that render their own
   * validation text below the field (mirrors the `error` prop on `Input`).
   */
  error?: boolean
  options: SelectOption[]
  required?: boolean
  size?: 'sm' | 'md' | 'lg'
  placeholder?: string
  loading?: boolean
}

const SelectShell = styled.div<{$hasError: boolean; $disabled: boolean; $size: 'sm' | 'md' | 'lg'}>`
  position: relative;
  display: flex;
  align-items: center;
  background: ${({$disabled}) => ($disabled ? 'var(--neutral-100)' : 'var(--bg-card)')};
  /* --border-default matches the DS Input/DatePicker border (design review:
     "different line color than the other lines") and satisfies §3a (a control
     whose border is its only indicator needs 3:1). Was --neutral-300 (< 3:1). */
  border: 1px solid ${({$hasError}) => ($hasError ? 'var(--error-500)' : 'var(--border-default)')};
  border-radius: var(--radius-sm);
  height: ${({$size}) =>
    $size === 'sm'
      ? 'var(--input-height-sm)'
      : $size === 'lg'
      ? 'var(--input-height-lg)'
      : 'var(--input-height-md)'};
  transition: all var(--motion-fast) var(--motion-ease);

  &:hover {
    border-color: ${({$disabled}) => ($disabled ? 'var(--neutral-300)' : 'var(--neutral-400)')};
  }

  &:focus-within {
    border-color: var(--brand-cta);
    box-shadow: 0 0 0 3px var(--brand-100);
  }
`

const NativeSelect = styled.select`
  flex: 1;
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  padding: 0 36px 0 14px;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: var(--text-primary);
  height: 100%;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    color: var(--text-disabled);
  }

  /* Native <option> styling — browsers expose limited control over the
     OS-rendered dropdown list. These apply where supported (most desktop
     browsers) to keep the open list on-brand (elevated surface, DS text). */
  option {
    background: var(--bg-elevated);
    color: var(--text-primary);
    font-family: var(--font-sans);
    font-size: var(--fs-body);
    padding: var(--space-2) var(--space-3);
  }

  option:disabled {
    color: var(--text-disabled);
  }
`

const Chevron = styled.div`
  position: absolute;
  right: 12px;
  pointer-events: none;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
`

const FieldLabel = styled.label`
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  color: var(--neutral-800);
  display: block;
  margin-bottom: 6px;
`

/* Outer field container. position: relative anchors the absolutely-positioned
   FieldHelper below the field (mirrors FieldParts.FieldWrapper) so error/helper
   text sits in the gap and right-aligns like the text Input — no layout shift. */
const FieldRoot = styled.div`
  position: relative;
  width: 100%;
`

const FieldHelper = styled.span<{$error?: boolean}>`
  font-size: var(--fs-caption);
  color: ${({$error}) => ($error ? 'var(--error-500)' : 'var(--text-secondary)')};
  display: block;
  position: absolute;
  right: 0;
  top: 100%;
  margin-top: var(--space-1);
  ${({$error}) => $error && 'text-align: right;'}
`

/* ============================================================
   SHARED DROPDOWN PRIMITIVE
   The custom popover, option list, keyboard navigation, and
   click-outside behavior shared by Select (click-only) and
   Combobox (type-ahead). This is the ONE place the dropdown
   *mechanics* live — both components compose it but keep their
   own trigger + public API, so neither couples to the other.
   ============================================================ */

export interface DropdownListboxItem {
  value: string
  label: string
  disabled?: boolean
  /** Optional rich display node rendered instead of the plain `label` text. */
  labelContent?: React.ReactNode
}

const DropdownListboxWrapper = styled.div`
  position: relative;
  width: 100%;
`

const DropdownPopover = styled.div`
  position: fixed;
  box-sizing: border-box;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-elevated);
  overflow-y: auto;
  /* Above the modal layer (DS Modal = --z-modal, MUI Dialog = 1300) so the
     dropdown isn't painted behind a dialog when a Select opens inside one.
     Still below toast/tooltip. */
  z-index: calc(var(--z-modal) + 1);
`

const DropdownOptionItem = styled.button<{$highlighted: boolean; $selected: boolean}>`
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  background: ${({$highlighted}) => ($highlighted ? 'var(--bg-hover)' : 'transparent')};
  border: none;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: ${({$selected}) => ($selected ? 'var(--primary-700)' : 'var(--text-primary)')};
  font-weight: ${({$selected}) => ($selected ? 'var(--fw-semibold)' : 'var(--fw-regular)')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);

  /* The label's Tooltip wrapper: a long label ends in "…", and only then shows its tooltip. */
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const DropdownEmpty = styled.div`
  padding: var(--space-3) 14px;
  font-size: var(--fs-body);
  color: var(--text-secondary);
  text-align: center;
`

/**
 * useDropdownListbox — owns the dropdown *mechanics* (open state,
 * highlight index, click-outside, shared keyboard navigation).
 * Triggers (Select's box, Combobox's input) wire their keydown to
 * `handleKeyDown` and render `<DropdownListbox>` inside `wrapperRef`.
 */
function useDropdownListbox<T extends DropdownListboxItem>({
  options,
  onSelect,
  onClose,
  onEnterNoMatch,
}: {
  options: T[]
  onSelect: (opt: T) => void
  /** Called whenever the popover closes (commit free-text, clear query, etc.) */
  onClose?: () => void
  /** Enter pressed with no valid highlighted option (e.g. freeSolo commit) */
  onEnterNoMatch?: () => void
}) {
  const [open, setOpen] = useState(false)
  const [highlighted, setHighlighted] = useState(0)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // The popover is portaled to <body>, so it's outside wrapperRef — track it
  // separately so a click inside the open list isn't treated as "outside".
  const popoverRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => {
    onClose?.()
    setOpen(false)
  }, [onClose])

  // Central selection path — used by both click and keyboard so both
  // commit the value AND close the popover consistently.
  const selectOption = useCallback(
    (opt: T) => {
      onSelect(opt)
      setOpen(false)
    },
    [onSelect],
  )

  // A press outside the field and its list closes it; one on a row's portaled tooltip bubble is
  // inside, through the list's React tree (`markPressInside`, on the popover).
  const markPressInside = useOutsidePress(open, [wrapperRef, popoverRef], close)

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) {
        if (e.key === 'Enter' || e.key === 'ArrowDown') {
          e.preventDefault()
          setOpen(true)
        }
        return
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setHighlighted((h) => Math.min(h + 1, options.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setHighlighted((h) => Math.max(h - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const opt = options[highlighted]
        if (opt && !opt.disabled) {
          selectOption(opt)
        } else {
          onEnterNoMatch?.()
          setOpen(false)
        }
      } else if (e.key === 'Escape') {
        e.preventDefault()
        close()
      }
    },
    [open, options, highlighted, selectOption, onEnterNoMatch, close],
  )

  return {
    open,
    setOpen,
    highlighted,
    setHighlighted,
    wrapperRef,
    popoverRef,
    markPressInside,
    handleKeyDown,
    selectOption,
  }
}

/**
 * DropdownListbox — presentational popover + option rows. Purely
 * visual; the parent owns open/highlight state and selection. Shared
 * by Select and Combobox so the open list looks identical everywhere.
 */
function DropdownListbox<T extends DropdownListboxItem>({
  open,
  options,
  value,
  highlighted,
  onHighlight,
  onSelect,
  listboxId,
  loading = false,
  emptyText = 'No matches',
  anchorRef,
  popoverRef,
  onPressInside,
  onRequestClose,
}: {
  open: boolean
  options: T[]
  value: string | null
  highlighted: number
  onHighlight: (index: number) => void
  onSelect: (opt: T) => void
  listboxId: string
  loading?: boolean
  emptyText?: string
  /** Element the popover is positioned against (the field box). */
  anchorRef: React.RefObject<HTMLElement>
  /** Ref attached to the portaled popover (for the hook's click-outside check). */
  popoverRef: React.RefObject<HTMLDivElement>
  /** The hook's `markPressInside`: a press in the list's React tree (a row's bubble) is inside. */
  onPressInside: () => void
  /** Closes the owner when its trigger leaves the visible viewport. */
  onRequestClose: () => void
}) {
  const position = useAdaptiveDropdownPosition({
    open,
    anchorRef,
    popoverRef,
    itemSelector: '[role="option"]',
    itemCount: options.length,
    matchAnchorWidth: true,
    onAnchorHidden: onRequestClose,
  })

  // EX-4: only scroll on keyboard navigation, not on mouse-hover highlights.
  // Track the source of the last highlight change via a ref.
  const itemsRef = useRef<(HTMLButtonElement | null)[]>([])
  const isMouseHighlightRef = useRef(false)

  const handleMouseHighlight = useCallback(
    (idx: number) => {
      isMouseHighlightRef.current = true
      onHighlight(idx)
    },
    [onHighlight],
  )

  useEffect(() => {
    if (isMouseHighlightRef.current) {
      isMouseHighlightRef.current = false
      return // mouse hover — do not scroll
    }
    // EX-4: defensive optional-chain in case scrollIntoView is absent (jsdom, old Safari)
    itemsRef.current[highlighted]?.scrollIntoView?.({block: 'nearest'})
  }, [highlighted])

  if (!open) return null

  const popoverStyle: React.CSSProperties = {
    top: position?.top ?? 0,
    left: position?.left ?? 0,
    width: position?.width,
    maxHeight: position?.maxHeight,
    visibility: position ? 'visible' : 'hidden',
  }

  return createPortal(
    <DropdownPopover
      ref={popoverRef}
      role="listbox"
      id={listboxId}
      style={popoverStyle}
      onMouseDown={onPressInside}>
      {loading ? (
        <DropdownEmpty>Loading…</DropdownEmpty>
      ) : options.length === 0 ? (
        <DropdownEmpty>{emptyText}</DropdownEmpty>
      ) : (
        options.map((opt, idx) => (
          <DropdownOptionItem
            key={opt.value}
            ref={(el) => {
              itemsRef.current[idx] = el
            }}
            type="button"
            role="option"
            aria-selected={opt.value === value}
            $highlighted={idx === highlighted}
            $selected={opt.value === value}
            disabled={opt.disabled}
            onMouseEnter={() => handleMouseHighlight(idx)}
            onClick={() => onSelect(opt)}>
            {/* A copy of the row: shown only while the row is cut off, adding no description,
                beside the list (side="right"), where it covers no other row. */}
            <Tooltip content={opt.label} side="right" block truncatedOnly describeChild={false}>
              {opt.labelContent ?? <span>{opt.label}</span>}
            </Tooltip>
            {opt.value === value && (
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{flexShrink: 0}}>
                <path
                  d="M3 7L6 10L11 4"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </DropdownOptionItem>
        ))
      )}
    </DropdownPopover>,
    document.body,
  )
}

/* Read-only trigger box for the (now custom-popover) Select. Mirrors
   the NativeSelect text styling so the closed state looks unchanged. */
const SelectTrigger = styled.button<{$placeholder: boolean}>`
  flex: 1;
  /* Allow the trigger to shrink below its content so the > span ellipsis actually
     engages — without this a long selected value overflows and forces a scrollbar. */
  min-width: 0;
  display: flex;
  align-items: center;
  appearance: none;
  background: transparent;
  border: none;
  outline: none;
  padding: 0 36px 0 14px;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: ${({$placeholder}) => ($placeholder ? 'var(--neutral-500)' : 'var(--text-primary)')};
  height: 100%;
  cursor: pointer;
  text-align: left;

  > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:disabled {
    cursor: not-allowed;
    color: var(--text-disabled);
  }
`

let selectCounter = 0
/**
 * Select — styled dropdown. Renders a custom popover (shared with
 * Combobox) rather than a native <select>, so the open list is fully
 * on-brand. Public API is unchanged: controlled via `value` +
 * `onChange` (event-shaped, so `e.target.value` keeps working). A
 * visually-hidden native <select> preserves the forwarded ref, the
 * `name` for form submission, and accessibility.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      errorText,
      error,
      options = [],
      required,
      size = 'md',
      placeholder,
      disabled,
      loading,
      id,
      value,
      onChange,
      name,
      onBlur,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) => {
    const [resolvedId] = useState(() => id ?? `select-${++selectCounter}`)
    const hasError = Boolean(errorText) || Boolean(error)
    const currentValue = value != null ? String(value) : ''
    const selectedOption = options.find((o) => o.value === currentValue)
    const listboxId = `${resolvedId}-listbox`
    const resolvedDisabled = Boolean(disabled || loading)

    const emitChange = useCallback(
      (newValue: string) => {
        if (!onChange) return
        const target = {value: newValue, name: name ?? ''}
        // Synthesize a select ChangeEvent so existing `e.target.value` consumers keep working.
        onChange({target, currentTarget: target} as unknown as React.ChangeEvent<HTMLSelectElement>)
      },
      [onChange, name],
    )

    const {
      open,
      setOpen,
      highlighted,
      setHighlighted,
      wrapperRef,
      popoverRef,
      markPressInside,
      handleKeyDown,
      selectOption,
    } = useDropdownListbox<SelectOption>({
      options,
      onSelect: (opt) => emitChange(opt.value),
    })
    const anchorRef = useRef<HTMLDivElement>(null)

    // When opening, highlight the currently-selected option.
    useEffect(() => {
      if (!open) return
      const idx = options.findIndex((o) => o.value === currentValue)
      setHighlighted(idx >= 0 ? idx : 0)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open])

    return (
      <FieldRoot ref={wrapperRef}>
        {label && (
          <FieldLabel htmlFor={resolvedId}>
            {label}
            {required && (
              <span style={{color: 'var(--error-500)'}} aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </FieldLabel>
        )}
        <DropdownListboxWrapper ref={anchorRef}>
          <SelectShell $hasError={hasError} $disabled={resolvedDisabled} $size={size}>
            <SelectTrigger
              type="button"
              id={resolvedId}
              role="combobox"
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={listboxId}
              aria-invalid={hasError || undefined}
              aria-describedby={errorText ? `${resolvedId}-error` : undefined}
              aria-label={ariaLabel}
              disabled={resolvedDisabled}
              $placeholder={!selectedOption}
              onClick={() => !resolvedDisabled && setOpen((o) => !o)}
              onKeyDown={handleKeyDown}
              onBlur={onBlur as unknown as React.FocusEventHandler<HTMLButtonElement>}>
              {selectedOption?.labelContent ?? (
                <span>{selectedOption?.label ?? placeholder ?? 'Select…'}</span>
              )}
            </SelectTrigger>
            <Chevron>
              {loading ? (
                <Spinner size={14} role="status" aria-label="Loading options" />
              ) : (
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M4 6L8 10L12 6"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </Chevron>
          </SelectShell>
          <DropdownListbox
            open={open}
            options={options}
            value={currentValue}
            loading={loading}
            highlighted={highlighted}
            onHighlight={setHighlighted}
            onSelect={selectOption}
            listboxId={listboxId}
            anchorRef={anchorRef}
            popoverRef={popoverRef}
            onPressInside={markPressInside}
            onRequestClose={() => setOpen(false)}
          />
        </DropdownListboxWrapper>
        {/* Hidden native select: preserves forwarded ref, form submission via `name`,
            and remains the test/automation target (data-testid + ...rest land here).
            Its onChange routes through emitChange so userEvent.selectOptions() keeps
            firing the consumer's onChange exactly as before. Options mirror the real
            labels so existing tests that query option text / selectOptions keep working.
            NOTE: this means a label can appear twice in the DOM (visible trigger +
            hidden option). Tests asserting the *visible* value should scope to the
            trigger via getByRole('combobox') rather than getByText(). */}
        <VisuallyHidden>
          <select
            {...(rest as Record<string, unknown>)}
            ref={ref}
            name={name}
            value={currentValue}
            required={required}
            disabled={resolvedDisabled}
            aria-hidden="true"
            tabIndex={-1}
            onChange={(e) => emitChange(e.target.value)}>
            {placeholder && <option value="">{placeholder}</option>}
            {options.map((o) => (
              <option key={o.value} value={o.value} disabled={o.disabled}>
                {o.label}
              </option>
            ))}
          </select>
        </VisuallyHidden>
        {errorText && (
          <FieldHelper id={`${resolvedId}-error`} $error role="alert">
            {errorText}
          </FieldHelper>
        )}
        {!errorText && helperText && <FieldHelper>{helperText}</FieldHelper>}
      </FieldRoot>
    )
  },
)
Select.displayName = 'Select'

/* ============================================================
   COMBOBOX
   Searchable dropdown. For long lists, async, type-ahead.
   Uses a custom popover, not native <select>.
   ============================================================ */

export interface ComboboxOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ComboboxProps {
  label?: string
  helperText?: string
  errorText?: string
  options: ComboboxOption[]
  value: string | null
  onChange: (value: string | null) => void
  placeholder?: string
  required?: boolean
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
  /** Async-friendly: pass a callback to fetch options on query change. */
  onSearch?: (query: string) => void
  loading?: boolean
  /** When true, allows arbitrary text values not in the options list. */
  freeSolo?: boolean
  /**
   * When false, the field is click-to-open only (no text filtering) —
   * behaves like a styled MUI Select rather than a type-ahead. Default true.
   */
  searchable?: boolean
  /**
   * Text shown in the open list when nothing matches the query. Defaults to the string this
   * component used to hardcode, so existing call sites keep their current wording.
   */
  emptyText?: string
  /**
   * Lands on the input, which is the test and automation target. Declared explicitly because
   * this props interface is CLOSED — it has no rest spread, so a testid passed by a caller was
   * silently dropped and callers had to wrap the field in a div to keep a handle on it.
   */
  'data-testid'?: string
  /** Accessible name, for fields whose visible label is not wired up with htmlFor. */
  'aria-label'?: string
}

let comboboxCounter = 0
export const Combobox = ({
  label,
  helperText,
  errorText,
  options = [],
  value,
  onChange,
  placeholder = 'Select…',
  required,
  disabled,
  size = 'md',
  onSearch,
  loading = false,
  freeSolo = false,
  searchable = true,
  emptyText = 'No matches',
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
}: ComboboxProps) => {
  const [id] = useState(() => `combobox-${++comboboxCounter}`)
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const listboxId = `${id}-listbox`

  const selectedOption = options.find((o) => o.value === value)
  // Non-searchable mode shows the full list (click-to-open like MUI Select).
  const filteredOptions =
    !searchable || onSearch
      ? options
      : options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))

  // Shared dropdown mechanics (open/highlight/keyboard/click-outside).
  const {
    open,
    setOpen,
    highlighted,
    setHighlighted,
    wrapperRef,
    popoverRef,
    markPressInside,
    handleKeyDown,
    selectOption,
  } = useDropdownListbox<ComboboxOption>({
    options: filteredOptions,
    onSelect: (opt) => {
      onChange(opt.value)
      setQuery('')
    },
    // Commit free-text on close (click-outside / Escape).
    onClose: () => {
      if (freeSolo && query.trim()) onChange(query.trim())
      setQuery('')
    },
    // Enter with no valid option commits free-text in freeSolo mode.
    onEnterNoMatch: () => {
      if (freeSolo && query.trim()) {
        onChange(query.trim())
        setQuery('')
      }
    },
  })

  const hasError = Boolean(errorText)
  const anchorRef = useRef<HTMLDivElement>(null)

  return (
    <FieldRoot ref={wrapperRef}>
      {label && (
        <FieldLabel htmlFor={id}>
          {label}
          {required && (
            <span style={{color: 'var(--error-500)'}} aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </FieldLabel>
      )}
      <DropdownListboxWrapper ref={anchorRef}>
        <SelectShell $hasError={hasError} $disabled={Boolean(disabled)} $size={size}>
          <NativeSelect
            as="input"
            ref={inputRef as React.Ref<HTMLSelectElement>}
            id={id}
            type="text"
            role="combobox"
            data-testid={dataTestId}
            aria-label={ariaLabel}
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete={searchable ? 'list' : 'none'}
            readOnly={!searchable}
            disabled={disabled}
            placeholder={placeholder}
            value={
              searchable
                ? open
                  ? query
                  : selectedOption?.label ?? (freeSolo ? value ?? '' : '')
                : selectedOption?.label ?? ''
            }
            onChange={(e) => {
              if (!searchable) return
              setQuery((e.target as HTMLInputElement).value)
              setHighlighted(0)
              if (onSearch) onSearch((e.target as HTMLInputElement).value)
              if (!open) setOpen(true)
            }}
            onFocus={() => searchable && setOpen(true)}
            onClick={() => {
              if (!searchable && !disabled) setOpen((o) => !o)
            }}
            onKeyDown={handleKeyDown}
            style={{paddingRight: 36, cursor: searchable ? 'text' : 'pointer'}}
          />
          <Chevron>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </Chevron>
        </SelectShell>
        <DropdownListbox
          open={open}
          options={filteredOptions}
          value={value}
          highlighted={highlighted}
          onHighlight={setHighlighted}
          onSelect={selectOption}
          listboxId={listboxId}
          loading={loading}
          emptyText={emptyText}
          anchorRef={anchorRef}
          popoverRef={popoverRef}
          onPressInside={markPressInside}
          onRequestClose={() => setOpen(false)}
        />
      </DropdownListboxWrapper>
      {errorText && (
        <FieldHelper $error role="alert">
          {errorText}
        </FieldHelper>
      )}
      {!errorText && helperText && <FieldHelper>{helperText}</FieldHelper>}
    </FieldRoot>
  )
}

/* ============================================================
   DATEPICKER
   Single date selection. Native input wrapper with token styling.
   For richer calendar UI (range, multi-month, presets), the dev
   can plug in react-day-picker or similar — but this baseline is
   accessible and stack-friendly out of the box.
   ============================================================ */

export interface DatePickerProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string
  helperText?: string
  errorText?: string
  required?: boolean
  size?: 'sm' | 'md' | 'lg'
}

let datePickerCounter = 0
export const DatePicker = forwardRef<HTMLInputElement, DatePickerProps>(
  ({label, helperText, errorText, required, size = 'md', disabled, id, ...rest}, ref) => {
    const [resolvedId] = useState(() => id ?? `datepicker-${++datePickerCounter}`)
    const hasError = Boolean(errorText)

    return (
      <FieldRoot>
        {label && (
          <FieldLabel htmlFor={resolvedId}>
            {label}
            {required && (
              <span style={{color: 'var(--error-500)'}} aria-hidden="true">
                {' '}
                *
              </span>
            )}
          </FieldLabel>
        )}
        <SelectShell $hasError={hasError} $disabled={Boolean(disabled)} $size={size}>
          <NativeSelect
            as="input"
            ref={ref as React.Ref<HTMLSelectElement>}
            id={resolvedId}
            type="date"
            disabled={disabled}
            aria-invalid={hasError || undefined}
            aria-describedby={errorText ? `${resolvedId}-error` : undefined}
            required={required}
            {...rest}
          />
        </SelectShell>
        {errorText && (
          <FieldHelper id={`${resolvedId}-error`} $error role="alert">
            {errorText}
          </FieldHelper>
        )}
        {!errorText && helperText && <FieldHelper>{helperText}</FieldHelper>}
      </FieldRoot>
    )
  },
)
DatePicker.displayName = 'DatePicker'

/* ============================================================
   DATE RANGE PICKER
   Two DatePickers side by side with validation that end > start.
   ============================================================ */

export interface DateRangePickerProps {
  label?: string
  startValue: string
  endValue: string
  onChange: (range: {start: string; end: string}) => void
  errorText?: string
  required?: boolean
  disabled?: boolean
  size?: 'sm' | 'md' | 'lg'
}

const RangeRow = styled.div`
  display: flex;
  gap: var(--space-2);
  align-items: stretch;

  & > * {
    flex: 1;
  }
`

const RangeSeparator = styled.span`
  align-self: center;
  color: var(--text-disabled);
  flex: 0;
`

export const DateRangePicker = ({
  label,
  startValue,
  endValue,
  onChange,
  errorText,
  required,
  disabled,
  size = 'md',
}: DateRangePickerProps) => {
  const handleStart = (val: string) => onChange({start: val, end: endValue})
  const handleEnd = (val: string) => onChange({start: startValue, end: val})

  return (
    <FieldRoot>
      {label && (
        <FieldLabel>
          {label}
          {required && <span style={{color: 'var(--error-500)'}}> *</span>}
        </FieldLabel>
      )}
      <RangeRow>
        <DatePicker
          aria-label="Start date"
          value={startValue}
          onChange={(e) => handleStart(e.target.value)}
          disabled={disabled}
          size={size}
        />
        <RangeSeparator>→</RangeSeparator>
        <DatePicker
          aria-label="End date"
          value={endValue}
          onChange={(e) => handleEnd(e.target.value)}
          disabled={disabled}
          size={size}
          min={startValue || undefined}
        />
      </RangeRow>
      {errorText && (
        <FieldHelper $error role="alert">
          {errorText}
        </FieldHelper>
      )}
    </FieldRoot>
  )
}

/* ============================================================
   TOAST
   ToastProvider + useToast hook pattern.
   Auto-dismiss, screen-reader announced, stacks bottom-right.
   ============================================================ */

export type ToastTone = 'info' | 'success' | 'warning' | 'error'

/**
 * Opt-in (STFFLG-4184): one link-styled control after the toast's words, e.g. "Open Job Orders"
 * after "Invitation accepted…". Pressing it runs `onClick` and dismisses the toast. A convenience
 * only: whatever it opens must be reachable without it, since the toast leaves on its own.
 */
export interface ToastAction {
  label: string
  onClick: () => void
  'data-testid'?: string
}

export interface Toast {
  id: string
  tone: ToastTone
  title: string
  description?: string
  /** Default 5000ms; 10000ms with an `action`, so there is time to reach it. */
  durationMs?: number
  action?: ToastAction
}

interface ToastContextValue {
  toast: (input: Omit<Toast, 'id'>) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const ToastViewport = styled.div`
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  z-index: var(--z-toast);
  max-width: 420px;
  pointer-events: none;
`

const toastToneStyles = {
  info: css`
    background: var(--info-100);
    border-left: 4px solid var(--info-500);
    color: var(--info-800);
  `,
  success: css`
    background: var(--success-100);
    border-left: 4px solid var(--success-500);
    color: var(--success-800);
  `,
  warning: css`
    background: var(--warning-100);
    border-left: 4px solid var(--warning-500);
    color: var(--warning-800);
  `,
  error: css`
    background: var(--error-100);
    border-left: 4px solid var(--error-500);
    color: var(--error-800);
  `,
}

const ToastShell = styled.div<{$tone: ToastTone}>`
  pointer-events: auto;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-elevated);
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: var(--space-3);
  animation: ${slideUp} var(--motion-base) var(--motion-ease);
  ${({$tone}) => toastToneStyles[$tone]}
`

const ToastContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

const ToastTitle = styled.div`
  font-weight: var(--fw-semibold);
`

const ToastDescription = styled.div`
  font-size: var(--fs-caption);
  opacity: 0.9;
`

/* The optional action: text in the tone's own ink, underlined as a link (it navigates). */
const ToastActionButton = styled.button`
  align-self: flex-start;
  margin: var(--space-1) 0 0;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  font: inherit;
  font-weight: var(--fw-semibold);
  text-decoration: underline;
  cursor: pointer;
  border-radius: var(--radius-sm);

  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

/* Manual dismiss — complements the auto-hide so users can close a toast early.
   color: inherit keeps the X on the tone's text color (e.g. --success-800). */
const ToastCloseButton = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 2px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  opacity: 0.7;
  border-radius: var(--radius-sm);
  transition: opacity var(--motion-base) var(--motion-ease);

  &:hover {
    opacity: 1;
  }
  &:focus-visible {
    outline: none;
    box-shadow: var(--focus-ring);
  }
`

let toastCounter = 0
export const ToastProvider = ({children}: {children: ReactNode}) => {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (input: Omit<Toast, 'id'>) => {
      const id = `toast-${++toastCounter}`
      const duration = input.durationMs ?? (input.action ? 10000 : 5000)
      setToasts((prev) => [...prev, {...input, id}])
      setTimeout(() => dismiss(id), duration)
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={{toast, dismiss}}>
      {children}
      <ToastViewport role="region" aria-label="Notifications" aria-live="polite">
        {toasts.map((t) => (
          <ToastShell key={t.id} $tone={t.tone} role="status">
            <ToastContent>
              <ToastTitle>{t.title}</ToastTitle>
              {t.description && <ToastDescription>{t.description}</ToastDescription>}
              {t.action && (
                <ToastActionButton
                  type="button"
                  data-testid={t.action['data-testid']}
                  onClick={() => {
                    dismiss(t.id)
                    t.action?.onClick()
                  }}>
                  {t.action.label}
                </ToastActionButton>
              )}
            </ToastContent>
            <ToastCloseButton
              type="button"
              aria-label="Dismiss notification"
              onClick={() => dismiss(t.id)}>
              <X size={16} aria-hidden="true" />
            </ToastCloseButton>
          </ToastShell>
        ))}
      </ToastViewport>
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

/* ============================================================
   TOOLTIP
   Hover/focus triggered, with proper ARIA. Auto-positioned.
   WCAG 1.4.13: Escape dismisses it, the bubble can be hovered,
   and it stays until the pointer and focus have both left.
   ============================================================ */

export interface TooltipProps {
  content: ReactNode
  children: ReactNode
  side?: TooltipSide
  /** Render via portal to escape overflow:hidden ancestors (default true) */
  portal?: boolean
  /**
   * Render the trigger as a full-width block instead of shrink-to-content
   * inline-flex. Use when wrapping a full-width field (e.g. a Select) so the
   * field doesn't resize to match its selected value.
   */
  block?: boolean
  /**
   * Whether `content` becomes the child's aria-describedby. Unset, it does, unless `content` is a
   * string repeating the child's own text (a truncated cell, a pill's label), which a description
   * would only read twice. `true` forces it (e.g. a child named by aria-label); `false` stops it.
   */
  describeChild?: boolean
  /**
   * Show the bubble only while the child's text is cut off as rendered, read on each show, so a
   * resized column counts (STFFLG-4236). Unset, on exactly when `content` repeats the child's own
   * text (a `TableCell ellipsis` cell, a StatusPill's label): a copy of readable text is noise.
   * `true` gates a copy that isn't the exact text (an option's plain label over its rich row);
   * `false` keeps a repeat always on. Explanatory content (not the child's text) always shows.
   */
  truncatedOnly?: boolean
  /**
   * Toggletip mode, for InfoHint: a click (or Enter / Space on a button) pins the bubble open;
   * doing it again, Escape, a click outside or focus moving to another control closes it. The
   * click stays with the trigger rather than reaching a clickable row or card around it.
   */
  toggleOnClick?: boolean
  /**
   * Opt-in, for text inside a clickable card or row: a touch or pen tap that would
   * show the bubble (cut-off text, or explanatory content) pins it open, as a toggletip, and the tap
   * stays off the card. A press outside, Escape or moving focus closes it. A tap on text that fits
   * reaches the card as before; mouse and keyboard behave exactly as without it.
   */
  tapToReveal?: boolean
}

const TooltipWrapper = styled.span<{$block?: boolean}>`
  position: relative;
  display: ${({$block}) => ($block ? 'block' : 'inline-flex')};
  ${({$block}) => $block && 'width: 100%;'}
`

/* The bubble takes the pointer (WCAG 1.4.13 hoverable). A closed describing bubble stays in the
   DOM with the `hidden` attribute; the rule below keeps a page-level display from showing it. */
const bubbleBase = css`
  z-index: var(--z-tooltip);
  background: var(--bg-inverse);
  color: var(--text-inverse);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  white-space: normal;
  max-width: calc(var(--space-24) * 3 + var(--space-8));
  box-shadow: var(--shadow-elevated);

  &[hidden] {
    display: none;
  }
`

const TooltipBubble = styled.div<{$side: TooltipSide}>`
  position: absolute;
  ${bubbleBase}

  ${({$side}) =>
    $side === 'top'
      ? css`
          bottom: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
        `
      : $side === 'bottom'
      ? css`
          top: calc(100% + 6px);
          left: 50%;
          transform: translateX(-50%);
        `
      : $side === 'left'
      ? css`
          right: calc(100% + 6px);
          top: 50%;
          transform: translateY(-50%);
        `
      : css`
          left: calc(100% + 6px);
          top: 50%;
          transform: translateY(-50%);
        `}
`

/** Fixed to the viewport; `top` / `left` come in through `style`, as they change on scroll. A side
    bubble is as wide as its text (to the max) wherever it is, so near the viewport's edge it runs
    past it, which the Tooltip sees and answers by moving it to the other side (`sideWithRoom`). */
const PortalBubble = styled.div<{$side: TooltipSide}>`
  position: fixed;
  ${bubbleBase}

  ${({$side}) =>
    $side === 'top'
      ? css`
          transform: translateX(-50%) translateY(-100%);
        `
      : $side === 'bottom'
      ? css`
          transform: translateX(-50%);
        `
      : $side === 'left'
      ? css`
          width: max-content;
          transform: translateX(-100%) translateY(-50%);
        `
      : css`
          width: max-content;
          transform: translateY(-50%);
        `}
`

/** How long the bubble outlives the pointer (--motion-fast): long enough to cross the gap onto it. */
const TOOLTIP_HIDE_GRACE_MS = 150

let tooltipCounter = 0
export const Tooltip = ({
  content,
  children,
  side = 'top',
  portal = true,
  block = false,
  describeChild,
  truncatedOnly,
  toggleOnClick = false,
  tapToReveal = false,
}: TooltipProps) => {
  const [id] = useState(() => `tooltip-${++tooltipCounter}`)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const bubbleRef = useRef<HTMLDivElement>(null)
  const hideTimer = useRef<ReturnType<typeof setTimeout>>()
  const [hovered, setHovered] = useState(false)
  const [focused, setFocused] = useState(false)
  const [pinned, setPinned] = useState(false)
  // Escape (or closing a toggletip) keeps it shut until the pointer or focus leaves and returns.
  const [dismissed, setDismissed] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [pos, setPos] = useState<{top: number; left: number}>({top: 0, left: 0})
  // `side`, unless a side bubble had no room there when it showed (`sideWithRoom`).
  const [placedSide, setPlacedSide] = useState(side)
  const placedSideRef = useRef(side)
  const [cutOff, setCutOff] = useState(false)

  // Don't render an (empty) bubble when there's no content — callers sometimes
  // pass '' to conditionally disable the tooltip.
  const hasContent =
    content !== '' && content !== null && content !== undefined && content !== false

  // A describing bubble is always in the DOM (hidden while closed), so the child's
  // aria-describedby is constant and already there when focus lands. It is portaled, which the
  // server renderer can't do, so it joins after mount. A bubble that only repeats the child's
  // text is aria-hidden instead, so the text is read once.
  // A child can render the same text from its props (a StatusPill's label), which the element tree
  // doesn't show; the rendered trigger does. Read before a describing bubble joins the trigger.
  const [echoesRenderedText, setEchoesRenderedText] = useState(false)
  useLayoutEffect(() => {
    if (mounted || !triggerRef.current) return
    const text =
      typeof content === 'string' || typeof content === 'number' ? String(content).trim() : ''
    const rendered = renderedTextOf(triggerRef.current, bubbleRef.current).trim()
    setEchoesRenderedText(text !== '' && rendered === text)
  }, [content, children, mounted])
  const repeats = hasContent && (repeatsChildText(content, children) || echoesRenderedText)
  const describes = hasContent && (describeChild ?? !repeats)
  // A repeat is there for text the eye can't finish, so it shows only while that text is cut off,
  // read each time it goes to show (`place`). The text stays whole in the DOM for a screen reader.
  const onlyWhenCutOff = truncatedOnly ?? repeats
  const visible =
    hasContent && !dismissed && (hovered || focused || pinned) && (!onlyWhenCutOff || cutOff)
  // Layout, not passive: the trigger never paints without its aria-describedby. Declared after the
  // rendered-text check so that check reads the trigger before the bubble joins it.
  useLayoutEffect(() => {
    if (describes) setMounted(true)
  }, [describes])

  const GAP = 6

  const recalcPosition = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    let top = 0
    let left = 0

    switch (placedSideRef.current) {
      case 'top':
        top = rect.top - GAP
        left = rect.left + rect.width / 2
        break
      case 'bottom':
        top = rect.bottom + GAP
        left = rect.left + rect.width / 2
        break
      case 'left':
        top = rect.top + rect.height / 2
        left = rect.left - GAP
        break
      case 'right':
        top = rect.top + rect.height / 2
        left = rect.right + GAP
        break
      default:
        break
    }

    setPos({top, left})
  }, [])

  // Every way in (hover, focus, a toggletip click) places the fixed bubble in the same update
  // that shows it; while it is open, it follows the trigger through scroll and resize.
  const place = () => {
    if (onlyWhenCutOff && triggerRef.current) setCutOff(isTextTruncated(triggerRef.current))
    placedSideRef.current = side
    setPlacedSide(side)
    if (portal) recalcPosition()
  }
  // Once it shows, before it paints: a side bubble with no room on its side goes on the other.
  useLayoutEffect(() => {
    const bubbleEl = bubbleRef.current
    if (!visible || !portal || placedSide !== side || !bubbleEl || !triggerRef.current) return
    const next = sideWithRoom(
      side,
      triggerRef.current.getBoundingClientRect(),
      bubbleEl.getBoundingClientRect(),
    )
    if (next === side) return
    placedSideRef.current = next
    setPlacedSide(next)
    recalcPosition()
  }, [visible, portal, side, placedSide, pos, recalcPosition])
  useEffect(() => {
    if (!visible || !portal) return
    window.addEventListener('scroll', recalcPosition, true)
    window.addEventListener('resize', recalcPosition)
    return () => {
      window.removeEventListener('scroll', recalcPosition, true)
      window.removeEventListener('resize', recalcPosition)
    }
  }, [visible, portal, recalcPosition])

  // Capture phase, so this runs before the document listeners of a Modal, Drawer or Dropdown
  // around the trigger; stopPropagation keeps the same Escape from closing them too.
  useEffect(() => {
    if (!visible) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.stopPropagation()
      setDismissed(true)
      setPinned(false)
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [visible])

  // A pinned toggletip closes on a press anywhere but the trigger and the bubble.
  useEffect(() => {
    if (!pinned) return
    const onMouseDown = (event: MouseEvent) => {
      const target = event.target
      const inside =
        target instanceof Node &&
        (triggerRef.current?.contains(target) || bubbleRef.current?.contains(target))
      if (inside) return
      setPinned(false)
      setDismissed(true)
    }
    document.addEventListener('mousedown', onMouseDown)
    // A tap-revealed bubble also closes on a touch elsewhere, which not every browser turns into a
    // mousedown.
    if (tapToReveal) document.addEventListener('pointerdown', onMouseDown)

    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('pointerdown', onMouseDown)
    }
  }, [pinned, tapToReveal])

  const cancelHide = useCallback(() => clearTimeout(hideTimer.current), [])
  useEffect(() => cancelHide, [cancelHide])

  // The portaled bubble is a React child of the wrapper, so entering or leaving it reaches these
  // handlers as well; the grace covers the gap between trigger and bubble.
  const handleMouseEnter = () => {
    cancelHide()
    place()
    setHovered(true)
    setDismissed(false)
  }
  const handleMouseLeave = () => {
    cancelHide()
    hideTimer.current = setTimeout(() => setHovered(false), TOOLTIP_HIDE_GRACE_MS)
  }

  // Focus moving between elements inside the trigger is neither leaving nor returning.
  const movedWithin = (event: React.FocusEvent) =>
    event.relatedTarget instanceof Node &&
    Boolean(triggerRef.current?.contains(event.relatedTarget))
  const handleFocus = (event: React.FocusEvent) => {
    if (movedWithin(event)) return
    place()
    setFocused(true)
    setDismissed(false)
  }
  const handleBlur = (event: React.FocusEvent) => {
    if (movedWithin(event)) return
    setFocused(false)
    // Focus moving on to another control closes a pinned toggletip. Focus dropping to the page
    // does not: that is a click in the bubble, or a click outside, which closes it anyway.
    if (event.relatedTarget) setPinned(false)
  }

  // A click in the bubble arrives at the wrapper through the React tree; only the trigger counts.
  const onTrigger = (event: React.MouseEvent) =>
    event.target instanceof Node && Boolean(triggerRef.current?.contains(event.target))
  // The pointer behind the latest press ('mouse', 'touch', 'pen'; '' where the browser leaves it
  // unset). A click has no pointer type of its own in every browser, so it is read from here.
  const pressPointer = useRef('')
  const handlePointerDown = (event: React.PointerEvent) => {
    pressPointer.current = event.pointerType
  }
  // Pressing the trigger puts its bubble away: the bubble takes the pointer now, so one left open
  // after a click would sit over the controls around it. The next hover or focus brings it back.
  // Not for a tap: touch hovers, focuses and clicks in one gesture, so the tap is what shows it.
  // A keyboard click (detail 0) has no pointer behind it, whatever pressed last.
  const handlePressClick = (event: React.MouseEvent) => {
    const tapped =
      event.detail > 0 && (pressPointer.current === 'touch' || pressPointer.current === 'pen')
    // tapToReveal: a tap that would show the bubble pins it, and the card around it doesn't open.
    const tapShows = tapToReveal && tapped && hasContent && onTrigger(event)
    if (tapShows && (!onlyWhenCutOff || isTextTruncated(triggerRef.current!))) {
      handleToggleClick(event)
      return
    }
    if (onTrigger(event) && !tapped) setDismissed(true)
  }
  const handleToggleClick = (event: React.MouseEvent) => {
    if (!onTrigger(event)) return
    event.stopPropagation()
    // Enter or Space (a click with detail 0) toggles what a keyboard user sees: focus has already
    // shown the bubble, so the first press closes it. A pointer click pins it; hover showed it.
    if (event.detail === 0 && visible) {
      setPinned(false)
      setDismissed(true)
      return
    }
    place()
    setPinned(!pinned)
    setDismissed(pinned)
  }
  const handleToggleKeyDown = (event: React.KeyboardEvent) => {
    // Enter and Space press the trigger, and its click toggles; a row handling them shouldn't too.
    if (event.key === 'Enter' || event.key === ' ') event.stopPropagation()
  }

  const describedBy = describes && mounted ? id : undefined
  const childContent =
    describedBy && React.isValidElement<{'aria-describedby'?: string}>(children)
      ? React.cloneElement(children, {
          'aria-describedby': [children.props['aria-describedby'], describedBy]
            .filter(Boolean)
            .join(' '),
        })
      : children

  const bubbleA11y = describes
    ? {id, role: 'tooltip', hidden: !visible}
    : {'aria-hidden': true as const}
  // Clicking the bubble shouldn't act on a row or card that holds the tooltip.
  const stopClick = (event: React.MouseEvent) => event.stopPropagation()

  let bubble: ReactNode = null
  if (describes ? mounted : visible) {
    bubble = portal ? (
      createPortal(
        <PortalBubble
          ref={bubbleRef}
          $side={placedSide}
          style={{top: pos.top, left: pos.left}}
          onClick={stopClick}
          {...bubbleA11y}>
          {content}
        </PortalBubble>,
        document.body,
      )
    ) : (
      <TooltipBubble ref={bubbleRef} $side={side} onClick={stopClick} {...bubbleA11y}>
        {content}
      </TooltipBubble>
    )
  }

  return (
    <TooltipWrapper
      ref={triggerRef}
      $block={block}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onPointerDown={toggleOnClick ? undefined : handlePointerDown}
      onClick={toggleOnClick ? handleToggleClick : handlePressClick}
      onKeyDown={toggleOnClick ? handleToggleKeyDown : undefined}>
      {childContent}
      {bubble}
    </TooltipWrapper>
  )
}

/* ============================================================
   DROPDOWN MENU
   For row actions and contextual action lists.
   Keyboard navigable, click-outside closes.
   ============================================================ */

export interface DropdownMenuItem {
  id: string
  label: string
  icon?: ReactNode
  onClick: () => void
  disabled?: boolean
  destructive?: boolean
  divider?: boolean
}

export interface DropdownProps {
  trigger: ReactNode
  items: DropdownMenuItem[]
  align?: 'start' | 'end'
  /** Prefix for auto-generated data-testid on each menu item. Defaults to 'action'. */
  testIdPrefix?: string
}

const DropdownWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

/**
 * Portal-rendered dropdown menu. Uses position: fixed so the menu escapes
 * any ancestor overflow: hidden (e.g. TableCell). The shared adaptive positioner supplies
 * viewport-contained coordinates, max-width and height; the width itself stays in CSS.
 */
const PortalDropdownMenu = styled.div`
  position: fixed;
  box-sizing: border-box;
  width: max-content;
  min-width: 180px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-elevated);
  /* Above the modal layer so the menu isn't painted behind a dialog. */
  z-index: calc(var(--z-modal) + 1);
  padding: var(--space-1);
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
`

const DropdownItem = styled.button<{$destructive: boolean}>`
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) 10px;
  background: transparent;
  border: none;
  border-radius: var(--radius-sm);
  text-align: left;
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: ${({$destructive}) => ($destructive ? 'var(--error-500)' : 'var(--text-primary)')};
  cursor: pointer;
  width: 100%;

  svg,
  span[aria-hidden] {
    color: ${({$destructive}) => ($destructive ? 'var(--error-500)' : 'var(--text-secondary)')};
  }

  &:hover:not(:disabled) {
    background: ${({$destructive}) => ($destructive ? 'var(--error-100)' : 'var(--bg-hover)')};
  }

  &:focus-visible {
    outline: none;
    background: ${({$destructive}) => ($destructive ? 'var(--error-100)' : 'var(--bg-hover)')};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const DropdownDivider = styled.div`
  height: 1px;
  background: var(--divider);
  margin: var(--space-1) 0;
`

export const Dropdown = ({
  trigger,
  items,
  align = 'end',
  testIdPrefix = 'action',
}: DropdownProps) => {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const closeMenu = useCallback(() => setOpen(false), [])
  const position = useAdaptiveDropdownPosition({
    open,
    anchorRef: triggerRef,
    popoverRef: menuRef,
    itemSelector: '[role="menuitem"]',
    itemCount: items.length,
    align,
    onAnchorHidden: closeMenu,
  })

  // Click-outside: close if click is outside both trigger and portal menu
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return
      setOpen(false)
    }
    const escHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', escHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', escHandler)
    }
  }, [open])

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev)
  }, [])

  const menuContent = (
    <PortalDropdownMenu
      ref={menuRef}
      role="menu"
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        maxWidth: position?.maxWidth,
        maxHeight: position?.maxHeight,
        visibility: position ? 'visible' : 'hidden',
      }}>
      {items.map((item, idx) => (
        <React.Fragment key={item.id}>
          {item.divider && idx > 0 && <DropdownDivider />}
          <DropdownItem
            type="button"
            role="menuitem"
            data-testid={`${testIdPrefix}-${item.id}`}
            $destructive={Boolean(item.destructive)}
            disabled={item.disabled}
            onClick={(e) => {
              // The menu is portaled to <body>, but React portal events still
              // bubble through the React tree — so without stopping propagation
              // a menu-item click reaches an ancestor row's onClick (e.g. a
              // clickable table row that navigates). Stop it so action items
              // only run their own handler.
              e.stopPropagation()
              item.onClick()
              setOpen(false)
            }}>
            {item.icon}
            {item.label}
          </DropdownItem>
        </React.Fragment>
      ))}
    </PortalDropdownMenu>
  )

  return (
    <DropdownWrapper ref={triggerRef}>
      <span onClickCapture={handleToggle} aria-expanded={open} aria-haspopup="menu">
        {trigger}
      </span>
      {open && createPortal(menuContent, document.body)}
    </DropdownWrapper>
  )
}

/* ============================================================
   PAGINATION
   Current page = cyan filled circle. Single-instance cyan OK
   because only one page is current.
   ============================================================ */

export interface PaginationProps {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
  /** Page size selector. Optional. */
  pageSize?: number
  pageSizeOptions?: number[]
  onPageSizeChange?: (size: number) => void
  /** Total record count, displayed as "1–20 of 1,480". */
  totalCount?: number
  /** On the pager's root; absent whenever the pager renders nothing. */
  'data-testid'?: string
}

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-3) var(--space-4);
  gap: var(--space-4);
  flex-wrap: wrap;
  /* Full-width so the page-nav stays right-aligned (via space-between: optional
     meta on the left, nav on the right) no matter how a caller's footer wrapper
     is flexed. Keeps pagination consistent across the app. */
  width: 100%;
`

const PageList = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-1);
`

const PageButton = styled.button<{$current: boolean}>`
  min-width: 32px;
  height: 32px;
  padding: 0 var(--space-2);
  background: ${({$current}) => ($current ? 'var(--brand-cta)' : 'transparent')};
  color: ${({$current}) => ($current ? 'var(--text-on-brand)' : 'var(--neutral-700)')};
  border: none;
  border-radius: var(--radius-full);
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  font-weight: var(--fw-medium);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${({$current}) => ($current ? 'var(--brand-hover)' : 'var(--bg-hover)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--brand-100);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const PaginationMeta = styled.div`
  font-size: var(--fs-caption);
  color: var(--text-secondary);
`

/* At least as wide as its longest size (STFFLG-4263): the open list takes the field's width and
   draws the current size semibold beside a check mark, so a field sized to "100" cut that row to
   "1…". Each digit is 1ch in the Select's font, plus a spare ch for the semibold; 3.25rem is the
   field's padding and border (the row's padding, gap, check); 1rem is the list's scrollbar, which
   takes room on Windows and on macOS with a mouse when the list scrolls. */
const PageSizeField = styled.div<{$chars: number}>`
  flex: none;
  min-width: calc(${({$chars}) => $chars + 1}ch + 4.25rem);
  font-family: var(--font-sans);
  font-size: var(--fs-body);
`

const buildPageList = (current: number, total: number): (number | '…')[] => {
  if (total <= 7) return Array.from({length: total}, (_, i) => i + 1)
  const pages: (number | '…')[] = [1]
  if (current > 3) pages.push('…')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) {
    pages.push(i)
  }
  if (current < total - 2) pages.push('…')
  pages.push(total)
  return pages
}

export const Pagination = ({
  currentPage,
  totalPages,
  onChange,
  pageSize,
  pageSizeOptions,
  onPageSizeChange,
  totalCount,
  'data-testid': testId,
}: PaginationProps) => {
  const hasPageSizeSelector = !!(pageSizeOptions && onPageSizeChange)

  // A single page (or empty) has nothing to page through, so normally no pager renders (design
  // review: "remove pagination where a table has only 1 item"). BUT with a page-size selector the
  // controls stay: otherwise a size larger than the record count collapses to 1 page and hides the
  // selector, trapping the user at that size until a refresh. Fully hide only when there's no
  // selector to keep reachable, or there are genuinely no records.
  if (totalPages <= 1 && (!hasPageSizeSelector || totalCount === 0)) return null

  const pages = buildPageList(currentPage, totalPages)

  return (
    <PaginationWrapper data-testid={testId}>
      <Stack direction="row" gap="4" align="center">
        {hasPageSizeSelector && (
          <Stack direction="row" gap="2" align="center">
            <PaginationMeta as="span">Rows:</PaginationMeta>
            <PageSizeField $chars={Math.max(0, ...pageSizeOptions.map((n) => String(n).length))}>
              <Select
                options={pageSizeOptions.map((n) => ({value: String(n), label: String(n)}))}
                value={String(pageSize)}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                size="sm"
                aria-label="Rows per page"
              />
            </PageSizeField>
          </Stack>
        )}
        {totalCount !== undefined && pageSize !== undefined && (
          <PaginationMeta>
            {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, totalCount)} of{' '}
            {totalCount.toLocaleString()}
          </PaginationMeta>
        )}
      </Stack>
      {totalPages > 1 && (
        <PageList role="navigation" aria-label="Pagination">
          <PageButton
            type="button"
            $current={false}
            disabled={currentPage === 1}
            onClick={() => onChange(currentPage - 1)}
            aria-label="Previous page">
            ‹
          </PageButton>
          {pages.map((p, i) =>
            p === '…' ? (
              <span
                key={`ellipsis-${i}`}
                aria-hidden="true"
                style={{padding: '0 var(--space-1)', color: 'var(--text-disabled)'}}>
                …
              </span>
            ) : (
              <PageButton
                key={p}
                type="button"
                $current={p === currentPage}
                onClick={() => onChange(p)}
                aria-label={`Page ${p}`}
                aria-current={p === currentPage ? 'page' : undefined}>
                {p}
              </PageButton>
            ),
          )}
          <PageButton
            type="button"
            $current={false}
            disabled={currentPage === totalPages}
            onClick={() => onChange(currentPage + 1)}
            aria-label="Next page">
            ›
          </PageButton>
        </PageList>
      )}
    </PaginationWrapper>
  )
}

/* ============================================================
   SKELETON
   For loading states. Slow pulse, respects reduced motion.
   ============================================================ */

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`

export interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  width?: string | number
  height?: string | number
  circle?: boolean
}

const StyledSkeleton = styled.div<{
  $width?: string | number
  $height?: string | number
  $circle: boolean
}>`
  background: var(--neutral-200);
  border-radius: ${({$circle}) => ($circle ? 'var(--radius-full)' : 'var(--radius-sm)')};
  width: ${({$width}) => (typeof $width === 'number' ? `${$width}px` : $width ?? '100%')};
  height: ${({$height}) => (typeof $height === 'number' ? `${$height}px` : $height ?? '16px')};
  animation: ${pulse} 1.2s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    opacity: 0.7;
  }
`

export const Skeleton = ({width, height, circle = false, ...rest}: SkeletonProps) => (
  <StyledSkeleton
    $width={width}
    $height={height}
    $circle={circle}
    role="status"
    aria-label="Loading"
    {...rest}
  />
)

/* ============================================================
   BREADCRUMB
   ============================================================ */

export {Breadcrumb, type BreadcrumbItem, type BreadcrumbProps} from './components/Breadcrumb'

/* ============================================================
   CHECKBOX
   Navy checked state (mass-instance rule).
   ============================================================ */

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string
  indeterminate?: boolean
}

const CheckboxWrapper = styled.label<{$disabled: boolean}>`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  cursor: ${({$disabled}) => ($disabled ? 'not-allowed' : 'pointer')};
  font-family: var(--font-sans);
  font-size: var(--fs-body);
  color: ${({$disabled}) => ($disabled ? 'var(--text-disabled)' : 'var(--text-primary)')};
  user-select: none;
  min-height: 24px;
`

const HiddenCheckbox = styled.input.attrs({type: 'checkbox'})`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;

  &:focus-visible + span {
    box-shadow: var(--focus-ring);
  }
`

const CheckboxVisual = styled.span<{$checked: boolean; $indeterminate: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid
    ${({$checked, $indeterminate}) =>
      $checked || $indeterminate ? 'var(--primary-700)' : 'var(--neutral-400)'};
  background: ${({$checked, $indeterminate}) =>
    $checked || $indeterminate ? 'var(--primary-700)' : 'var(--bg-card)'};
  border-radius: var(--radius-sm);
  color: var(--text-on-brand);
  transition: all var(--motion-fast) var(--motion-ease);
  flex-shrink: 0;
`

/** The tick or the dash inside the box. */
function CheckboxGlyph({checked, indeterminate}: {checked: boolean; indeterminate: boolean}) {
  if (indeterminate) {
    return (
      <svg width="10" height="2" viewBox="0 0 10 2" fill="none">
        <path d="M0 1L10 1" stroke="currentColor" strokeWidth="2" />
      </svg>
    )
  }
  if (!checked) return null
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path
        d="M2.5 6L5 8.5L9.5 4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export interface CheckboxMarkProps {
  checked: boolean
  indeterminate?: boolean
}

/**
 * The Checkbox's box alone, for a row that is itself the control — a listbox option, whose state
 * its `aria-selected` carries. Decorative (aria-hidden, not focusable): never use it in place of a
 * real `Checkbox`.
 */
export function CheckboxMark({checked, indeterminate = false}: CheckboxMarkProps) {
  return (
    <CheckboxVisual aria-hidden="true" $checked={checked} $indeterminate={indeterminate}>
      <CheckboxGlyph checked={checked} indeterminate={indeterminate} />
    </CheckboxVisual>
  )
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({label, indeterminate, checked, disabled, ...rest}, ref) => {
    const innerRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
      if (innerRef.current) {
        innerRef.current.indeterminate = Boolean(indeterminate)
      }
    }, [indeterminate])

    return (
      <CheckboxWrapper
        $disabled={Boolean(disabled)}
        /**
         * The VISIBLE, clickable handle.
         *
         * `data-testid` stays on the input so React Testing Library's getByTestId + fireEvent.click
         * keep working — but that input is the 1x1 clip-rect visually-hidden pattern, so a browser
         * driver refuses to click it ("element is being covered by another element"). Real users are
         * unaffected: this wrapper is a <label>, so clicking the visible box has always toggled it.
         * Only automation needs the extra handle.
         *
         * Without this, NO Cypress test could tick any checkbox in the app — including the eSign
         * consent gate, which is the first thing a signer meets. That is a large part of why the
         * eSign suite drives the API and never renders a page.
         */
        data-testid={
          (rest as Record<string, unknown>)['data-testid']
            ? `${(rest as Record<string, unknown>)['data-testid'] as string}-box`
            : undefined
        }>
        <HiddenCheckbox
          ref={(el) => {
            innerRef.current = el
            if (typeof ref === 'function') ref(el)
            else if (ref) ref.current = el
          }}
          checked={checked}
          disabled={disabled}
          aria-checked={indeterminate ? 'mixed' : checked}
          {...rest}
        />
        <CheckboxVisual $checked={Boolean(checked)} $indeterminate={Boolean(indeterminate)}>
          <CheckboxGlyph checked={Boolean(checked)} indeterminate={Boolean(indeterminate)} />
        </CheckboxVisual>
        {label && <span>{label}</span>}
      </CheckboxWrapper>
    )
  },
)
Checkbox.displayName = 'Checkbox'

/* ============================================================
   RADIO
   Navy selected state (mass-instance rule).
   ============================================================ */

export interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string
}

const HiddenRadio = styled.input.attrs({type: 'radio'})`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + span {
    box-shadow: var(--focus-ring);
  }
`

const RadioVisual = styled.span<{$checked: boolean}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: 1.5px solid ${({$checked}) => ($checked ? 'var(--primary-700)' : 'var(--neutral-400)')};
  background: var(--bg-card);
  border-radius: 50%;
  flex-shrink: 0;
  transition: all var(--motion-fast) var(--motion-ease);

  &::after {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--primary-700);
    opacity: ${({$checked}) => ($checked ? 1 : 0)};
    transition: opacity var(--motion-fast) var(--motion-ease);
  }
`

export const Radio = forwardRef<HTMLInputElement, RadioProps>(
  ({label, checked, disabled, ...rest}, ref) => (
    <CheckboxWrapper $disabled={Boolean(disabled)}>
      <HiddenRadio ref={ref} checked={checked} disabled={disabled} {...rest} />
      <RadioVisual $checked={Boolean(checked)} />
      {label && <span>{label}</span>}
    </CheckboxWrapper>
  ),
)
Radio.displayName = 'Radio'

/* ============================================================
   TOGGLE
   Navy ON state (mass-instance rule).
   ============================================================ */

export interface ToggleProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: string
}

const ToggleTrack = styled.span<{$checked: boolean; $disabled: boolean}>`
  display: inline-flex;
  align-items: center;
  width: 36px;
  height: 20px;
  border-radius: var(--radius-full);
  background: ${({$checked}) => ($checked ? 'var(--primary-700)' : 'var(--neutral-300)')};
  padding: 2px;
  transition: background var(--motion-fast) var(--motion-ease);
  opacity: ${({$disabled}) => ($disabled ? 0.5 : 1)};
  flex-shrink: 0;
`

const ToggleThumb = styled.span<{$checked: boolean}>`
  display: inline-block;
  width: 16px;
  height: 16px;
  background: var(--bg-card);
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transform: translateX(${({$checked}) => ($checked ? '16px' : '0')});
  transition: transform var(--motion-fast) var(--motion-ease);
`

const HiddenToggle = styled.input.attrs({type: 'checkbox', role: 'switch'})`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;

  &:focus-visible + span {
    box-shadow: var(--focus-ring);
  }
`

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(
  ({label, checked, disabled, ...rest}, ref) => (
    <CheckboxWrapper $disabled={Boolean(disabled)}>
      <HiddenToggle
        ref={ref}
        checked={checked}
        disabled={disabled}
        aria-checked={Boolean(checked)}
        {...rest}
      />
      <ToggleTrack $checked={Boolean(checked)} $disabled={Boolean(disabled)}>
        <ToggleThumb $checked={Boolean(checked)} />
      </ToggleTrack>
      {label && <span>{label}</span>}
    </CheckboxWrapper>
  ),
)
Toggle.displayName = 'Toggle'
